import { getApps, initializeApp } from 'firebase/app';
import { getMessaging, getToken, isSupported, onMessage } from 'firebase/messaging';

let configPromise;
let foregroundBound = false;

const REQUIRED_FIREBASE_KEYS = ['apiKey', 'projectId', 'messagingSenderId', 'appId'];

function hasRequiredFirebaseConfig(config) {
  if (!config || typeof config !== 'object') return false;
  return REQUIRED_FIREBASE_KEYS.every((key) => Boolean(String(config[key] || '').trim()));
}

export async function loadFirebaseConfig() {
  if (!configPromise) {
    configPromise = fetch('/firebase-config.json', { cache: 'no-store' })
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);
  }
  const config = await configPromise;
  return hasRequiredFirebaseConfig(config) ? config : null;
}

export async function getFirebaseMessagingClient() {
  const supported = await isSupported().catch(() => false);
  if (!supported) {
    return { supported: false, configured: false, messaging: null, config: null };
  }

  const config = await loadFirebaseConfig();
  if (!config) {
    return { supported: true, configured: false, messaging: null, config: null };
  }

  const app = getApps().length ? getApps()[0] : initializeApp(config);
  return {
    supported: true,
    configured: true,
    messaging: getMessaging(app),
    config,
  };
}

export async function getFirebasePushToken() {
  const client = await getFirebaseMessagingClient();
  if (!client.supported || !client.configured || !client.messaging) {
    return {
      token: '',
      supported: client.supported,
      configured: client.configured,
    };
  }

  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return { token: '', supported: client.supported, configured: client.configured };
  }

  if (!('serviceWorker' in navigator)) {
    return { token: '', supported: client.supported, configured: client.configured };
  }

  const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
  const vapidKey = (process.env.REACT_APP_FIREBASE_VAPID_KEY || client.config.vapidKey || '').trim();
  const token = await getToken(client.messaging, {
    vapidKey: vapidKey || undefined,
    serviceWorkerRegistration: registration,
  }).catch(() => '');

  return {
    token: token || '',
    supported: client.supported,
    configured: client.configured,
  };
}

export async function bindForegroundMessages(handler) {
  const client = await getFirebaseMessagingClient();
  if (!client.supported || !client.configured || !client.messaging || foregroundBound) {
    return;
  }
  foregroundBound = true;
  onMessage(client.messaging, (payload) => {
    if (typeof handler === 'function') {
      handler(payload);
    }
  });
}
